/// <reference types="createjs" />
/// <reference types="easeljs" />

import {useMediaFrameElements} from '~/composables/studio/useMediaFrameElements'

const {getFrameDocument} = useMediaFrameElements()

type PenType = null | 'black' | 'gold'

let mediaFrame: HTMLIFrameElement
let mediaStage: createjs.Stage
let penCanvasInitialized = false

let cjs: any // This is a reference to the lib used in the iframe
let penCanvas: null|HTMLCanvasElement = null
let penStage: createjs.Stage

let currentPenShape: createjs.Shape
let penShapes: createjs.Shape[] = []
let shapePoints: createjs.Point[] = []

let penDownListener: Function
let penMoveListener: Function
let penMoveTickListener: Function
let penUpListener: Function

const activePen = ref<PenType>(null)
const canUndo = ref<boolean>(false)

function setup(frame: HTMLIFrameElement) {
  if (frame !== mediaFrame) {
    mediaFrame = frame
    penCanvasInitialized = false
  }
}

function initializePenCanvas() {
  penCanvasInitialized = true

  let mediaFrameDocument = getFrameDocument(mediaFrame)

  if (!mediaFrameDocument) {
    console.warn('No document')
    return
  }

  penCanvas = mediaFrameDocument.createElement('canvas')
  penCanvas.dataset.layerName = 'pen'

  penCanvas.style.position = 'absolute'
  mediaFrameDocument.body.appendChild(penCanvas)

  onMediaStage(mediaFrameDocument.defaultView)
}

function onMediaStage(w: any) {
  mediaStage = w.stage
  cjs = w.createjs

  if (!mediaStage || !cjs) {
    console.warn('No stage or createjs')
    return
  }

  mediaStage.on('tick', function () {
    if (!penCanvas) {
      return
    }

    penCanvas.width = w.canvas.width
    penCanvas.height = w.canvas.height

    penCanvas.style.width = w.canvas.style.width
    penCanvas.style.height = w.canvas.style.height

    // noinspection JSSuspiciousNameCombination
    penStage.scaleX = penStage.scaleY = mediaStage.scaleX
  })

  penStage = new cjs.Stage(penCanvas)
  // @ts-ignore
  mediaStage.on('tick', penStage)
  cjs.Touch.enable(penStage)
}

watch(activePen, (newPen) => {

  if (newPen && !penCanvasInitialized) {
    initializePenCanvas()
  }

  if (!penCanvas || !penStage) {
    return
  }

  penCanvas.style.pointerEvents = activePen.value ? '' : 'none'
  penCanvas.style.cursor = activePen.value ? 'crosshair' : ''

  penStage.off('stagemousedown', penDownListener)

  if (activePen.value) {
    penDownListener = penStage.on('stagemousedown', onPenDown)
  }
})

function onPenDown(o: Object) {
  const e = o as createjs.MouseEvent

  penMoveListener = penStage.on('stagemousemove', onPenMove)
  penMoveTickListener = mediaStage.on('tick', onPenMoveTick)
  penUpListener = penStage.on('stagemouseup', onPenUp)

  currentPenShape = new cjs.Shape()
  shapePoints = [penStage.globalToLocal(e.stageX, e.stageY)]

  penStage.addChild(currentPenShape)
  penShapes.push(currentPenShape)
}

function onPenMove(o: Object) {
  const e = o as createjs.MouseEvent
  shapePoints.push(penStage.globalToLocal(e.stageX, e.stageY))
}

function onPenMoveTick() {
  redrawPolygon(currentPenShape.graphics, shapePoints)
}

function redrawPolygon(gr: createjs.Graphics, ar: createjs.Point[]) {
  const step = 2

  if (ar.length - 1 < step) {
    return
  }

  const firstMidPt = new cjs.Point((ar[0].x + ar[step].x) / 2, (ar[0].y + ar[step].y) / 2)

  gr.clear()
  gr.beginStroke(activePen.value === 'gold' ? '#F7941D' : 'black').setStrokeStyle(2, 'round', 'round')
  gr.moveTo(firstMidPt.x, firstMidPt.y)

  for (let i = step; i < ar.length - 2; i += step) {
    if (ar[i + step]) {
      const midPt = new cjs.Point((ar[i].x + ar[i + step].x) / 2, (ar[i].y + ar[i + step].y) / 2)
      gr.quadraticCurveTo(ar[i].x, ar[i].y, midPt.x, midPt.y)
    }
  }

  gr.endStroke()
}

function onPenUp() {
  penStage.off('stagemousemove', penMoveListener)
  mediaStage.off('tick', penMoveTickListener)
  penStage.off('stagemouseup', penUpListener)
  onPenShapesChange()
}

function onPenShapesChange() {
  canUndo.value = penShapes.length > 0
}

function undo() {
  const s = penShapes.pop()
  if (s) {
    s.parent.removeChild(s)
  }
  onPenShapesChange()
}

function clear() {
  for (let i = 0; i < penShapes.length; i++) {
    penShapes[i].parent.removeChild(penShapes[i])
  }
  penShapes = []
  onPenShapesChange()

  activePen.value = null
}

export const useMediaPenTools = () => {
  return {
    setup,
    activePen,
    canUndo,
    undo,
    clear,
  }

}
